import type { ReactNode } from "react";
import type { TokenId } from "@/model/tokens";
import { cn } from "@/ui/kit/cn";
import { C } from "./tok";
import { v } from "./vars";

const h = (key: string) => `herdr.${key}` as TokenId;

function Row({
  active,
  selected,
  children,
}: {
  active?: boolean;
  selected?: boolean;
  children: ReactNode;
}) {
  const bg = selected ? h("selection_bg") : active ? h("active_row_bg") : undefined;
  return (
    <div
      className="flex items-center gap-[1ch] whitespace-pre px-[1ch]"
      style={{ background: bg ? v(bg) : undefined }}
      data-token={bg}
    >
      {children}
    </div>
  );
}

function Dot({ state }: { state: "working" | "blocked" | "done" | "idle" }) {
  const tok = { working: h("yellow"), blocked: h("red"), done: h("green"), idle: h("overlay1") }[
    state
  ];
  return <C fg={tok}>●</C>;
}

export function HerdrSidebar({ compact }: { compact?: boolean }) {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-[0.6lh] py-[0.5lh]",
        compact ? "w-[22ch]" : "w-[28ch]",
      )}
      style={{ background: v(h("sidebar_bg")), color: v(h("text")) }}
      data-token={h("sidebar_bg")}
    >
      <div>
        <div
          className="px-[1ch] text-[0.85em]"
          style={{ color: v(h("subtext0")) }}
          data-token={h("subtext0")}
        >
          SPACES
        </div>
        <Row active>
          <C fg={h("accent")}>▌</C>
          <C fg={h("text")} bold>
            themes
          </C>
          <C fg={h("overlay1")}>3 tabs</C>
        </Row>
        <Row>
          <span> </span>
          <C fg={h("text")}>homelab</C>
        </Row>
        <Row selected>
          <span> </span>
          <C fg={h("text")}>dotfiles</C>
        </Row>
      </div>
      <div>
        <div className="px-[1ch] text-[0.85em]" style={{ color: v(h("subtext0")) }}>
          AGENTS
        </div>
        <Row active>
          <Dot state="working" />
          <C fg={h("text")}>claude</C>
          <C fg={h("subtext0")}>themes · working</C>
        </Row>
        <Row>
          <Dot state="blocked" />
          <C fg={h("text")}>cursor</C>
          <C fg={h("red")}>needs input</C>
        </Row>
        <Row>
          <Dot state="done" />
          <C fg={h("text")}>claude</C>
          <C fg={h("subtext0")}>homelab · done</C>
        </Row>
        {!compact && (
          <Row>
            <Dot state="idle" />
            <C fg={h("text")}>weechat</C>
            <C fg={h("overlay1")}>idle</C>
          </Row>
        )}
      </div>
    </div>
  );
}

export function HerdrTabs({ compact }: { compact?: boolean }) {
  const tabs = ["editor", "agents", "irc"];
  return (
    <div
      className="flex shrink-0 items-center whitespace-pre"
      style={{ background: v(h("surface_dim")) }}
      data-token={h("surface_dim")}
    >
      {tabs.map((t, i) => (
        <span
          key={t}
          className="px-[1.5ch]"
          data-token={i === 1 ? h("surface0") : h("surface_dim")}
          style={{
            background: i === 1 ? v(h("surface0")) : undefined,
            color: v(i === 1 ? h("accent") : h("subtext0")),
            fontWeight: i === 1 ? 700 : 400,
          }}
        >
          {i + 1} {t}
        </span>
      ))}
      <span className="flex-1" />
      {!compact && (
        <C fg={h("overlay1")} className="px-[1ch]">
          ctrl+t ? help
        </C>
      )}
      <C fg={h("subtext0")} className="px-[1ch]">
        14:32
      </C>
    </div>
  );
}

export function HerdrPane({
  focused,
  label,
  children,
  className,
}: {
  focused?: boolean;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const border = focused ? h("accent") : h("overlay0");
  return (
    <div
      className={cn("relative flex min-h-0 min-w-0 flex-col overflow-hidden border", className)}
      style={{ borderColor: v(border), background: v(h("panel_bg")) }}
      data-token={h("panel_bg")}
    >
      <div
        className="absolute top-[-1px] left-[1.5ch] z-10 whitespace-pre px-[0.5ch] text-[0.85em] leading-none"
        style={{ background: v(h("panel_bg")), color: v(focused ? h("accent") : h("overlay1")) }}
        data-token={border}
      >
        {label}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden pt-[0.55lh]">{children}</div>
    </div>
  );
}

export function HerdrToast() {
  return (
    <div
      className="absolute right-3 bottom-3 z-20 w-[36ch] rounded-sm border px-[1ch] py-[0.4lh] shadow-lg"
      style={{ background: v(h("surface0")), borderColor: v(h("accent")) }}
      data-token={h("surface0")}
    >
      <div className="flex justify-between">
        <C fg={h("accent")} bold>
          WeeChat · #neovim
        </C>
        <C fg={h("overlay1")}>now</C>
      </div>
      <C fg={h("text")}>lnu: the new palette looks great</C>
    </div>
  );
}

export function HerdrPreview() {
  return (
    <div
      className="relative flex h-full text-[13px] leading-[1.55]"
      style={{ background: v(h("panel_bg")), color: v(h("text")) }}
    >
      <HerdrSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <HerdrTabs />
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-[2px] p-[2px]">
          <HerdrPane focused label="claude · working">
            <div className="space-y-1 p-[1ch]">
              <C fg={h("text")}>focused pane: border uses accent</C>
              <div>
                <C fg={h("subtext0")}>subtext0 · secondary text</C>
              </div>
              <div className="flex gap-[1ch]">
                {["red", "peach", "yellow", "green", "teal", "blue", "mauve"].map((k) => (
                  <C key={k} fg={h(k)}>
                    {k}
                  </C>
                ))}
              </div>
            </div>
          </HerdrPane>
          <HerdrPane label="cursor · needs input">
            <div className="p-[1ch]">
              <C fg={h("overlay1")}>unfocused pane: overlay0 border</C>
            </div>
          </HerdrPane>
          <HerdrPane label="weechat" className="col-span-2">
            <div className="flex gap-[1ch] p-[1ch]">
              {[
                "surface_dim",
                "surface0",
                "surface1",
                "overlay0",
                "overlay1",
                "active_row_bg",
                "selection_bg",
              ].map((k) => (
                <div
                  key={k}
                  className="flex flex-col items-center gap-0.5 font-sans text-[10px]"
                  data-token={h(k)}
                >
                  <div
                    className="h-6 w-14 rounded-sm ring-1 ring-black/20"
                    style={{ background: v(h(k)) }}
                  />
                  <span style={{ color: v(h("subtext0")) }}>{k}</span>
                </div>
              ))}
            </div>
          </HerdrPane>
        </div>
      </div>
      <HerdrToast />
    </div>
  );
}
